/**
 *     ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AddEditRegionComponent } from '../../../regions';
import { ChangeDetectorRef, Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { GslbApiService } from '../../../../http';
import { GslbRealServiceModel } from '../../../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { LbErrorService, WizardControllerStateService, WizardService, WizardTemplateService } from '../../../../services';
import { RegionModel } from '../../../../models/regionsModel';
import { RegionsApiService } from '../../../../http/regions.api.service';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin } from 'rxjs';
import { ipValidator } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-gslb-real-server',
    templateUrl: './gslb-real-server.component.html',
    styleUrls: ['./gslb-real-server.component.scss']
})
export class GslbRealServerComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Reference to embedded component container.
     */
    @ViewChild('regionsContainer', { read: ViewContainerRef, static: true })
    public regionsContainer: ViewContainerRef;

    /**
     * Are we part of creating a service or just adding or editing a real server?
     */
    public isAddEdit: boolean = true;

    /**
     * Embedded page open.
     */
    public isEmbeddedPageOpen: boolean = false;

    /**
     * Store the edit id.
     * @private
     */
    public editId: string;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        membersWeight: [],
        regions: [],
    };

    /**
     * Our available made members from the API.
     */
    public realServers: GslbRealServiceModel[] = [];

    /**
     * List of keys of the dropdown items to hide.
     */
    public realServerDropdownHiddenKeys: string[] = [];

    /**
     * Store the region api response.
     * @private
     */
    private regions: RegionModel[] = [];

    /**
     * Store component reference.
     * @private
     */
    private regionComponentReference: ComponentRef<any>;

    /**
     * To store the callback for the wizard once the add is complete.
     * @private
     */
    private callBack: (...args: any[]) => void;

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        private gslbApiService: GslbApiService,
        private regionsApiService: RegionsApiService,
        private wizardControllerStateService: WizardControllerStateService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
    }

    ngOnInit(): void {
        this.componentTitle = WizardTitleEnum.REALSERVERS;
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Get rows.
     */
    get regionRows(): FormArray {
        return this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.regions') as FormArray;
    }

    /**
     * Add subject alt row in the form.
     */
    public addRegion(region?: any): void {
        this.regionRows.push(this.createRegion(region));
    }

    /**
     * Delete subject alt row from the form.
     */
    public removeRegion(i: number): void {
        this.realServerDropdownHiddenKeys?.splice(i, 1);
        this.realServerDropdownHiddenKeys = [...this.realServerDropdownHiddenKeys];
        this.regionRows.removeAt(i);
    }

    /**
     * Create regions.
     * @param region
     */
    public createRegion(region?: any): FormGroup {
        this.realServerDropdownHiddenKeys.push(null);

        return this.formBuilder.group({
            id: [region?.id ? region.id : null],
        });
    }

    /**
     * Update regions list in the dropdown.
     * @param key
     * @param index
     */
    public updateRegions(key: string, index: number): void {
        this.realServerDropdownHiddenKeys[index] = key;
        // Force the dropdown component to update its internal list.
        if (this.dropdown.regions.length <= this.realServerDropdownHiddenKeys.length) {
            this.realServerDropdownHiddenKeys = [...this.realServerDropdownHiddenKeys];
        }
    }

    /**
     * Navigate back to the correct page.
     */
    public returnToParentPage(row?: any): void {
        if (row) {
            this.updateControllerStates(row);
            this.callBack(row);
        } else {
            const formRow: any = this.serviceForm.get(this.subStepEnum.GENERAL).value;
            this.deleteRealServer(formRow);
            this.callBack();
        }
    }

    /**
     * Set up regions as an embedded component.
     */
    public setUpEmbeddedComponent(): void {
        this.isEmbeddedPageOpen = true;

        const embeddedComponent: ComponentRef<any> = this.regionsContainer.createComponent(AddEditRegionComponent);
        embeddedComponent.instance.embedded = true;
        embeddedComponent.instance.pageType = 'addRegion';
        embeddedComponent.instance.callBack = (newRegion: any): void => {
            this.updatePage(newRegion);
        };

        this.regionComponentReference = embeddedComponent;
    }

    /**
     * Cancel embedded view.
     * @private
     */
    private updatePage(newRegion?: any): void {
        this.isEmbeddedPageOpen = false;
        this.regionComponentReference.destroy();

        if (newRegion) {
            this.dropdown.regions.push(<DropdownModel>{ key: newRegion.id, value: newRegion.name });
            const lastValue: any = this.regionRows.value[this.regionRows.length - 1];
            if (lastValue?.id !== null && lastValue?.id !== '- Select -') {
                this.addRegion(newRegion);
            } else {
                this.regionRows.at(this.regionRows.length - 1).get('id').setValue(newRegion.id);
            }
        }
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setupFormGroup(): void {
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                // General
                id: ['temp-' + Date.now() + Math.random()], // <- Used outside of this component.
                name: [null, [Validators.required]],
                ip: [null, [Validators.required, ipValidator]],
                monitorIp: [null, [Validators.required, ipValidator]],
                weight: [null, [Validators.required, Validators.min(0), Validators.max(10)]],
                regions: this.formBuilder.array([this.createRegion()])
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.REALSERVERS);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL).valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    this.utilsService.debounce('realservers', (): void => {
                        if (this.serviceForm.valid && this.isAddEdit) {
                            this.updateControllerStates(values);
                            this.wizardService.isSectionValidEmitter.next(this.serviceForm.valid);
                        }

                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, values)) {
                            this.serviceForm.markAsPristine();
                        }
                    });
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.ip').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (ip: string): void => {
                    this.checkIPForDuplicates(ip);
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (name: string): void => {
                    this.checkNameForDuplicates(name, this.realServers);
                }
            });
    }

    /**
     * Check for duplicate ips.
     * @param ip
     * @private
     */
    private checkIPForDuplicates(ip: string): void {
        const ipField: AbstractControl = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.ip');
        const match: GslbRealServiceModel = this.realServers?.find(item => item.ip === ip);

        if (match) {
            ipField.setErrors({ 'duplicateIp': 'IP address already in use' });
        } else {
            if (ipField.hasError('duplicateIp')) {
                ipField.setErrors({ 'duplicateIp': null });
            }
        }
    }

    /**
     * Setup wizard subscribers.
     * @private
     */
    private setupWizardSubscribers(): void {
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (advancedState: string): void => {
                    this.advancedMode = advancedState;
                    this.toggleAdvanceModeInputs();
                }
            });
    }

    /**
     * Toggle the inputs that are only part of advanced mode.
     * @private
     */
    private toggleAdvanceModeInputs(): void {
        const advancedMode = this.advancedMode === 'advanced';
        this.inputStateStore['weight'] = { hidden: !advancedMode };
        const state = advancedMode ? 'enable' : 'disable';
        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.weight')[state]();
    }

    /**
     * Loads both the floating IP groups and Interfaces and set the values.
     * @private
     */
    private initLoading(): void {
        const apiCalls = {
            realservers: this.gslbApiService.getRealServers(),
            regions: this.regionsApiService.getRegions()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                    next: (dataGroup: object): void => {
                        const serviceError: any = this.getErrorForService(WizardTitleEnum.REALSERVERS);
                        const template: any = this.getTemplate(WizardTitleEnum.REALSERVERS, serviceError, this.editId);

                        const services: GslbRealServiceModel[] = this.formatServices(dataGroup['realservers'], WizardTitleEnum.REALSERVERS);
                        this.realServers = this.filterServicesByName(template, services);
                        this.regions = dataGroup['regions'];

                        this.patchTemplateToForm(template);
                        // Once everything is loaded check to see if there are any error in the controller state
                        if (serviceError?.error?.length) {
                            this.setErrorStateOnPage(serviceError.error);
                        }
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error(
                            'MembersComponent:initLoading',
                            'Unable to get members from the API', error.message
                        );
                    }
                }
            );
    }

    /**
     * Initialises the form values.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdownContent();

        // Populate our patch
        const templatePatch: any = this.createTemplatePatch(template);

        // Check if we are in advanced mode and set the correct states on the inputs before patching
        this.advancedMode = this.wizardService.wizardMode;
        this.toggleAdvanceModeInputs();

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.REALSERVERS);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        this.initialFormData = templatePatch;

        // Patching can change the view, so we should kick the change detector.
        this.cdr.detectChanges();

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent(): void {
        for (let i = 0; i < 11; i++) {
            this.dropdown.membersWeight.push(<DropdownModel>{ key: i, value: i.toString() });
        }

        this.dropdown.regions = this.utilsService.convertArrayToDropDown(this.regions, 'id', 'name');
    }

    /**
     * Check for error state.
     * @param stepError
     * @private
     */
    private setErrorStateOnPage(stepError: any[]): void {
        this.loadedWithErrors(stepError);
    }

    /**
     * Update the controller state
     * @param newRealServer
     * @private
     */
    private updateControllerStates(newRealServer: any): void {
        const realServers: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.REALSERVERS}`);
        const realServerIndex: any = realServers
            ? realServers.findIndex((item: any): boolean => item.id.value === newRealServer.id)
            : -1;

        const template: any = this.outputFormValues(newRealServer);

        if (realServers && realServerIndex !== -1) {
            this.wizardControllerStateService.updateServiceInControllerStateById(
                this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, newRealServer.id, template
            );
        } else {
            this.wizardControllerStateService.addServiceToControllerState(
                this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, template
            );
        }

        // Only update the backend if we are adding a new real server, and we are not adding/editing a backend.
        if (this.isAddEdit && !this.utilsService.isServiceId(newRealServer.id)) {
            this.updateBackendControllerState(newRealServer);
        }
    }

    /**
     * Delete ripId from backend.
     * @param row
     * @private
     */
    private deleteRealServer(row: any): void {
        const backends: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.BACKENDS}`);
        const backend: any = backends[this.templateIndex];

        if (backend?.id?.value) {
            this.wizardControllerStateService.deleteServiceItemInControllerStateById(
                this.wizardService.controllerState, WizardTitleEnum.BACKENDS, backend.id.value, 'ripIds', row.id
            );
        }

        this.wizardControllerStateService.deleteServiceFromControllerStateByServiceId(
            this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, this.editId
        );
    }

    /**
     * Format output values.
     * @param newRealServer
     * @param template
     * @private
     */
    private outputFormValues(newRealServer: any, template?: undefined): any {
        const newTemplate: object = template ? template : {};
        for (const property in newRealServer) {
            if (newRealServer.hasOwnProperty(property)) {
                const visibility: any = this.inputStateStore[property] && this.inputStateStore[property].hidden;
                newTemplate[property] = {
                    value: newRealServer[property],
                    hidden: visibility !== undefined ? visibility : true
                };
            }
        }

        return newTemplate;
    }

    /**
     * Update backend controller state.
     * @param newRealServer
     * @private
     */
    private updateBackendControllerState(newRealServer: any): void {
        const backends: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.BACKENDS}`);
        if (!backends) {
            this.logService.error('updateBackendControllerState', 'No backends found.');

            return;
        }
        const backend: any = backends[backends.length > 1 ? this.templateIndex : 0];
        this.wizardControllerStateService.updateServiceItemInControllerStateById(
            this.wizardService.controllerState,
            WizardTitleEnum.BACKENDS,
            backend.id.value,
            'ripIds',
            [newRealServer.id]
        );
    }
}

